import React, { useState } from "react";
import { Paper } from "@mui/material";
import styled from "styled-components";
import LoaderWrapper from "../../../../../components/Loaders/LoaderWrapper";
import PrivateHeader from "../../../../../components/PrivateHeader";
import ErrorBlockWithPermissions from "../../../../../components/UI/ErrorBlockWithPermissions";
import { ENGAGEMENT_TYPES } from "../../../../../storage/constants";
import useVendorModuleReport from "../../../../../swr/hooks/VendorReports/useVendorModuleReport";
import EngagementTypeSelect from "../../reuseableComponents/EngagementTypeSelect";
import MembersInMultipleCompaniesWarning from "../../reuseableComponents/MembersInMultipleCompaniesWarning";
import CompanyTable from "./CompanyTable";
import EngagementByWeekBarChart from "./EngagementByWeekBarChart";
import FeedbackTable from "./FeedbackTable";
import ModuleDetails from "./ModuleDetails";
import SubmissionsTable from "./SubmissionsTable";
import QuestionsReport from "../QuestionsReport";
import { QUESTION_TYPES } from "../../../../../storage/constants";
const ModuleImg = styled.img`
  height: 60px;
  width: 60px;
  background-color: white;
  border-radius: 2px;
  object-fit: cover;
`;

const formatQuestions = (questions) => {
  // we need to format this response for the question breakdown report
  // there is no assigned/follower seperation for retail questions breakdown,
  // so we need to add these values together before passing to question breakdown
  if (!questions || questions?.length === 0) return;

  const formatted = questions.map((question) => {
    if (question.type === QUESTION_TYPES.TRUE_FALSE) {
      return {
        ...question,
        answer_distribution: {
          false:
            question.answer_distribution?.false
              ?.answers_of_assigned_submissions +
              question.answer_distribution?.false
                ?.answers_of_follower_submissions || 0,
          true:
            question.answer_distribution?.true
              ?.answers_of_assigned_submissions +
              question.answer_distribution?.true
                ?.answers_of_follower_submissions || 0,
        },
      };
    }
    if (question.type === QUESTION_TYPES.MULTIPLE_CHOICE) {
      return {
        ...question,
        answer_distribution: {
          a:
            question.answer_distribution?.a?.answers_of_assigned_submissions +
              question.answer_distribution?.a
                ?.answers_of_follower_submissions || 0,
          b:
            question.answer_distribution?.b?.answers_of_assigned_submissions +
              question.answer_distribution?.b
                ?.answers_of_follower_submissions || 0,
          c:
            question.answer_distribution?.c?.answers_of_assigned_submissions +
              question.answer_distribution?.c
                ?.answers_of_follower_submissions || 0,
          d:
            question.answer_distribution?.d?.answers_of_assigned_submissions +
              question.answer_distribution?.d
                ?.answers_of_follower_submissions || 0,
        },
      };
    }
    if (question.type === QUESTION_TYPES.SELECT_ALL) {
      const keys = Object.keys(question.answer_distribution);
      const cleanAnswerDist = {};
      keys.forEach(
        (key) =>
          (cleanAnswerDist[key] =
            question.answer_distribution[key].answers_of_assigned_submissions +
            question.answer_distribution[key].answers_of_follower_submissions),
      );

      return {
        ...question,
        answer_distribution: cleanAnswerDist,
      };
    }
  });

  return formatted;
};

const VendorProgress = ({ id }) => {
  const { isError, isLoading, data } = useVendorModuleReport(id);

  const [engagementType, setEngagementType] = useState(ENGAGEMENT_TYPES.ALL);

  if (isLoading) {
    return <LoaderWrapper text="Module Progress" />;
  }

  if (isError) {
    return (
      <>
        <PrivateHeader header="Reports" secondary="Module" />
        <ErrorBlockWithPermissions errorObj={isError} />
      </>
    );
  }

  const { quiz_info } = data;

  const formattedQuestions = formatQuestions(data?.questions_breakdown);

  return (
    <>
      <PrivateHeader header="Reports" secondary="Module" />
      <Paper
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: 10,
          backgroundColor: "white",
          borderRadius: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
          }}
        >
          <ModuleImg
            src={quiz_info.image}
            alt="module"
            style={{ border: "1px solid #eeeeee", borderRadius: 4 }}
          />
          <div
            style={{
              padding: "0px 12px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 20, fontWeight: 600, marginBottom: -2 }}>
              {quiz_info.name}
            </span>
            <span style={{ fontSize: 11, color: "darkgrey" }}>
              {quiz_info.description}
            </span>
          </div>
        </div>
        <EngagementTypeSelect
          engagementType={engagementType}
          setEngagementType={setEngagementType}
          isVendor
        />
      </Paper>
      <ModuleDetails data={data} engagementType={engagementType} />
      {data.users_with_team_members_in_multiple_companies > 0 && (
        <>
          <div style={{ height: 18 }} />
          <MembersInMultipleCompaniesWarning
            num={data.users_with_team_members_in_multiple_companies}
          />
        </>
      )}
      <div style={{ height: 16 }} />
      <CompanyTable
        companies={data.engaged_companies}
        engagementType={engagementType}
      />
      {data.all_submissions.length >= 1 && (
        <>
          <div style={{ height: 16 }} />

          <SubmissionsTable
            submissions={data.all_submissions}
            engagementType={engagementType}
            moduleName={quiz_info.name}
          />
          {data?.all_submissions.length >= 10 && (
            <>
              <div style={{ height: 16 }} />
              <EngagementByWeekBarChart
                data={data}
                engagementType={engagementType}
              />
            </>
          )}
        </>
      )}
      {data.feedback.length > 0 && (
        <>
          <div style={{ height: 16 }} />

          <FeedbackTable feedback={data.feedback} usersInfo={data.users_info} />
        </>
      )}
      {data.all_submissions.length > 1 && (
        <>
          <div style={{ minHeight: 16 }} />
          <QuestionsReport questions={formattedQuestions} />
        </>
      )}
    </>
  );
};

export default VendorProgress;
